import moment from 'moment';
import React from 'react';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import CallOut from '../../components/CallOut';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import useSeatLimits from '../../invitation/useSeatLimits';
import Link from '../../utils/router/Link';
import pricingPath, { openPricingModal } from '../../utils/url/pricingPath';

const BILLING_SUMMARY_FRAGMENT = gql(`
  # eslint-disable-next-line @graphql-eslint/require-selections
  fragment AdminBillingNotice_billingSummary on BillingSummary {
    status
    startAt
    trialEndAt
    periodEndAt
    # eslint-disable-next-line @graphql-eslint/require-selections
    plan {
      tier
    }
    planBill {
      billAt
    }
    source {
      id
    }
    pendingActionUrl
    chargeAutomatically
  }
`);

interface AdminBillingNoticeProps {
  billingSummary: DocumentType<typeof BILLING_SUMMARY_FRAGMENT>;
  isTrialEnded: boolean;
}

const AdminBillingNotice = ({
  billingSummary: {
    status,
    startAt,
    trialEndAt,
    periodEndAt,
    plan,
    planBill,
    source,
    pendingActionUrl,
    chargeAutomatically,
  },
  isTrialEnded,
}: AdminBillingNoticeProps) => {
  const dispatch = useAppDispatch();

  const { isSeatLimitMaxed } = useSeatLimits();

  const learnMore = (
    <>
      {' '}
      <Link
        to={pricingPath()}
        onClick={(e) => {
          e.preventDefault();
          openPricingModal(dispatch);
        }}
      >
        Learn about our plans here
      </Link>
      .
    </>
  );

  if (plan && plan.tier === 'FREE') {
    if (isSeatLimitMaxed)
      return (
        <CallOut variant="danger">
          Your team has reached the seat limit for our free plan. Please upgrade
          to a paid plan to add additional teammates.
        </CallOut>
      );

    // Trial was started at one point, and user is back on free plan
    if (startAt)
      return (
        <CallOut variant="danger">
          <b>Your trial has ended and you’re now on the Free plan.</b> Subscribe
          to Startup or Business to unlock private topics, unlimited users, and
          more. {learnMore}
        </CallOut>
      );

    return (
      <CallOut>
        <b>Your team is on the free plan.</b> Start a 14 day trial for Startup
        or Business plan to unlock private topics, unlimited users, and more.
        {learnMore}
      </CallOut>
    );
  }

  if (pendingActionUrl)
    return (
      <CallOut variant="danger">
        Your payment is still unconfirmed. Please{' '}
        <Link to={pendingActionUrl}>click here to confirm your payment</Link> to
        us with your bank.
      </CallOut>
    );

  switch (status) {
    case 'TRIALING':
      return (
        <CallOut variant="warning">
          Your team is on a trial, ending on {moment(trialEndAt).format('LL')}.
          Subscribe to continue benefiting from this plan after your trial
          expires. {learnMore}
        </CallOut>
      );

    case 'CANCELED':
      return (
        <CallOut variant="danger">
          Your subscription to Slab has been canceled. Reactivate your
          subscription anytime below.
        </CallOut>
      );

    case 'UNPAID':
      return (
        <CallOut variant="danger">
          Your account is temporarily locked due to problems with your billing.
          Please contact <Link to="mailto:support@slab.com">Slab Support</Link>{' '}
          so we can remedy the issue and reactivate your account.
        </CallOut>
      );

    case 'PAST_DUE': {
      if (isTrialEnded)
        return (
          <CallOut variant="danger">
            <b>Your trial has ended.</b> Subscribe to a plan to continue using
            Slab. {learnMore}
          </CallOut>
        );

      if (!chargeAutomatically)
        return (
          <CallOut variant="danger">
            <b>Payment past due.</b> Please settle unpaid invoices or contact{' '}
            <Link to="mailto:support@slab.com">support</Link> to continue using
            Slab.
          </CallOut>
        );

      if (source)
        return (
          <CallOut variant="danger">
            <b>Payment past due.</b> The credit card was declined. Please try a
            different card.
          </CallOut>
        );

      return (
        <CallOut variant="danger">
          <b>Payment past due.</b> Please add your payment information to
          continue using Slab.
        </CallOut>
      );
    }

    case 'ACTIVE':
      return (
        <CallOut>
          Your current subscription period runs through{' '}
          {moment(planBill.billAt).format('LL')}
        </CallOut>
      );

    case 'CANCELED_AND_ACTIVE':
      return (
        <CallOut variant="warning">
          <b>You have canceled your subscription.</b> You can continue to use
          Slab&apos;s {plan?.tier.toLowerCase()} features until{' '}
          {moment(periodEndAt).format('LL')}
          {isSeatLimitMaxed
            ? ''
            : ', after which you will be automatically downgraded to the free plan'}
          .
        </CallOut>
      );

    default:
      return null;
  }
};

export default AdminBillingNotice;
